import { KEY, tsKey } from './apply.js'
import { ROLE, KIND, CALL_STATE } from './constants.js'
import { normalizeSettings } from './permissions.js'

/**
 * Read-side helpers over the Hyperbee that `apply` maintains. Nothing here
 * mutates; the UI and @pine/core talk to a room exclusively through these.
 */
export class RoomView {
  constructor (bee) {
    this.bee = bee
  }

  async meta () {
    const node = await this.bee.get(KEY.meta())
    if (!node) return null
    const meta = node.value
    return { ...meta, settings: normalizeSettings(meta.settings) }
  }

  async member (account) {
    const node = await this.bee.get(KEY.member(account))
    return node ? node.value : null
  }

  async members () {
    const out = []
    for await (const node of this.bee.createReadStream({ gte: 'u/', lt: 'u0' })) {
      out.push(node.value)
    }
    out.sort((a, b) => (b.role - a.role) || (a.joinedAt - b.joinedAt))
    return out
  }

  async memberCount () {
    let n = 0
    for await (const _ of this.bee.createReadStream({ gte: 'u/', lt: 'u0' })) n++
    return n
  }

  async role (account) {
    const m = await this.member(account)
    return m ? m.role : null
  }

  async isMember (account) {
    return !!(await this.member(account))
  }

  async writers () {
    const out = []
    for await (const node of this.bee.createReadStream({ gte: 'w/', lt: 'w0' })) out.push(node.value)
    return out
  }

  /**
   * Newest-first page of messages. `before` is a timestamp cursor, so paging
   * stays correct even as older history streams in from other peers.
   */
  async messages ({ limit = 50, before = null, after = null, reverse = true } = {}) {
    const range = { gte: 'm/', lt: 'm0' }
    if (before) range.lt = KEY.message(before, '')
    if (after) range.gt = KEY.message(after, '￿')

    const out = []
    for await (const node of this.bee.createReadStream({ ...range, reverse, limit })) {
      out.push(node.value)
    }
    return reverse ? out.reverse() : out
  }

  async message (id) {
    const idx = await this.bee.get(KEY.messageIndex(id))
    if (!idx) return null
    const node = await this.bee.get(idx.value.key)
    return node ? node.value : null
  }

  /** The latest message time apply has accepted in this room; 0 before any. */
  async clock () {
    const node = await this.bee.get(KEY.clock())
    return (node && node.value && Number(node.value.max)) || 0
  }

  async lastMessage () {
    for await (const node of this.bee.createReadStream({ gte: 'm/', lt: 'm0', reverse: true, limit: 1 })) {
      return node.value
    }
    return null
  }

  async messageCount () {
    let n = 0
    for await (const _ of this.bee.createReadStream({ gte: 'm/', lt: 'm0' })) n++
    return n
  }

  async thread (rootId, { limit = 100 } = {}) {
    const out = []
    for await (const node of this.bee.createReadStream({
      gte: 'th/' + rootId + '/',
      lt: 'th/' + rootId + '0',
      limit
    })) {
      const m = await this.bee.get(node.value.key)
      if (m) out.push(m.value)
    }
    return out
  }

  /** Grouped reactions for one message: [{ emoji, count, accounts }]. */
  async reactions (messageId) {
    const grouped = new Map()
    for await (const node of this.bee.createReadStream({
      gte: 'rx/' + messageId + '/',
      lt: 'rx/' + messageId + '0'
    })) {
      // Account and emoji come from the value, not the key: a custom reaction
      // string is user input and must never have to survive key parsing.
      const { account, emoji, at } = node.value
      if (!grouped.has(emoji)) grouped.set(emoji, { emoji, count: 0, accounts: [], first: at })
      const g = grouped.get(emoji)
      g.count++
      g.accounts.push(account)
      if (at < g.first) g.first = at
    }
    return [...grouped.values()].sort((a, b) => (b.count - a.count) || (a.first - b.first))
  }

  async reactionsFor (messageIds) {
    const out = {}
    await Promise.all(messageIds.map(async (id) => { out[id] = await this.reactions(id) }))
    return out
  }

  async readState (account) {
    const node = await this.bee.get(KEY.read(account))
    return node ? node.value : null
  }

  async readStates () {
    const out = {}
    for await (const node of this.bee.createReadStream({ gte: 'rd/', lt: 'rd0' })) {
      out[node.key.slice(3)] = node.value
    }
    return out
  }

  /**
   * Number of messages after the caller's read marker, excluding their own.
   *
   * A call somebody else made that nobody picked up counts: it is the one
   * line in the chat that says "they tried to reach you", and as a plain
   * system line it lit nothing up — no badge, no notification — so neither
   * side of a call that never connected knew the other had tried.
   */
  async unreadCount (account) {
    const state = await this.readState(account)
    const from = state ? state.at : 0
    let n = 0
    for await (const node of this.bee.createReadStream({
      gt: KEY.message(from, '￿'),
      lt: 'm0'
    })) {
      const m = node.value
      if (m.author === account) continue
      if (m.deleted) continue
      if (m.kind === KIND.SYSTEM && !isMissedCall(m)) continue
      n++
    }
    return n
  }

  async pinned () {
    const out = []
    for await (const node of this.bee.createReadStream({ gte: 'pin/', lt: 'pin0', reverse: true })) {
      const m = await this.message(node.value.message)
      if (m) out.push({ ...node.value, message: m })
    }
    return out
  }

  async votes (pollId) {
    const tally = new Map()
    const voters = new Map()
    let total = 0
    for await (const node of this.bee.createReadStream({
      gte: 'pv/' + pollId + '/',
      lt: 'pv/' + pollId + '0'
    })) {
      const account = node.key.split('/')[2]
      total++
      voters.set(account, node.value.options)
      for (const i of node.value.options) tally.set(i, (tally.get(i) || 0) + 1)
    }
    return { tally: Object.fromEntries(tally), voters: Object.fromEntries(voters), total }
  }

  async calls ({ limit = 50 } = {}) {
    const out = []
    for await (const node of this.bee.createReadStream({ gte: 'call/', lt: 'call0', reverse: true, limit })) {
      out.push(node.value)
    }
    return out
  }

  /** Naive but adequate full-text scan; rooms are local and usually small. */
  async search (query, { limit = 50 } = {}) {
    const needle = String(query).toLowerCase().trim()
    if (!needle) return []
    const out = []
    for await (const node of this.bee.createReadStream({ gte: 'm/', lt: 'm0', reverse: true })) {
      const m = node.value
      if (m.deleted) continue
      const hay = (m.text || '') + ' ' + (m.attachments || []).map((a) => a.name).join(' ')
      if (hay.toLowerCase().includes(needle)) {
        out.push(m)
        if (out.length >= limit) break
      }
    }
    return out
  }

  async media ({ kinds = [KIND.IMAGE, KIND.VIDEO], limit = 200 } = {}) {
    const set = new Set(kinds)
    const out = []
    for await (const node of this.bee.createReadStream({ gte: 'm/', lt: 'm0', reverse: true })) {
      const m = node.value
      if (m.deleted || !set.has(m.kind)) continue
      out.push(m)
      if (out.length >= limit) break
    }
    return out
  }

  /** Everything the chat list needs, in one pass. */
  async summary (account) {
    const [meta, last, unread, memberCount] = await Promise.all([
      this.meta(),
      this.lastMessage(),
      this.unreadCount(account),
      this.memberCount()
    ])
    return { meta, last, unread, memberCount }
  }
}

/** A call that rang and was not picked up, as the chat records it. */
export function isMissedCall (message) {
  const extra = message && message.extra
  return !!(extra && extra.event === 'call' && extra.state === CALL_STATE.MISSED)
}

export { KEY, tsKey, ROLE }
